import React, { useState } from "react";
import {
  Avatar,
  Chip,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Button,
  Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate, useLocation } from "react-router-dom";
import { navigationConfig, ProductNav } from "../../config/navigation";
import { removeCookie } from "../../utils/cookie";
import { useAppSelector } from "../../store/reduxHooks";
import logo from "../../images/surgo-logo.svg";
import theme from "../../utils/theme";
import { isSuperUser } from "../../utils/adminSession";

interface SidebarProps {
  drawerWidth: number;
}

const Sidebar: React.FC<SidebarProps> = ({ drawerWidth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((state) => state.userSlice.user);
  const visibleNavigation = navigationConfig.filter(
    (product) => !product.requiresSuperUser || isSuperUser()
  );

  // Track which product sections are expanded - default expand based on current route
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    navigationConfig.forEach((product) => {
      initial[product.productId] = location.pathname.startsWith(product.basePath);
    });
    // If no section matches, expand the first one
    if (!Object.values(initial).some(Boolean)) {
      initial[navigationConfig[0]?.productId] = true;
    }
    return initial;
  });

  const handleToggle = (productId: string) => {
    setExpanded((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const handleLogout = () => {
    removeCookie();
    navigate("/");
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          background: "#FFFFFF",
          borderRight: "1px solid #EAEAEA",
          boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px 16px",
          gap: "8px",
          borderBottom: "1px solid #F0EDEB",
        }}
      >
        <img src={logo} alt="Surgo" style={{ width: "100%", height: "auto" }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: "Libre Franklin, sans-serif",
              fontWeight: 600,
              fontSize: "13px",
              lineHeight: "18px",
              color: "#878787",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Admin Panel
          </Typography>
          {process.env.REACT_APP_ENVIRONMENT && process.env.REACT_APP_ENVIRONMENT !== "production" && (
            <Chip
              label={process.env.REACT_APP_ENVIRONMENT?.toUpperCase()}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "10px",
                letterSpacing: "1px",
                height: 20,
                ...(process.env.REACT_APP_ENVIRONMENT === "development"
                  ? { backgroundColor: "#FFF3E0", color: "#E65100" }
                  : { backgroundColor: "#E3F2FD", color: "#1565C0" }),
              }}
            />
          )}
        </Box>
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {visibleNavigation.map((product: ProductNav) => (
          <React.Fragment key={product.productId}>
            <ListItemButton
              onClick={() => handleToggle(product.productId)}
              sx={{
                padding: "20px 16px",
                gap: "10px",
              }}
            >
              <ListItemText
                primary={product.label}
                primaryTypographyProps={{
                  sx: {
                    fontFamily: "Libre Franklin, sans-serif",
                    fontWeight: 600,
                    fontSize: "18px",
                    lineHeight: "28px",
                    letterSpacing: "0.25px",
                    color: "#000000",
                  },
                }}
              />
              {expanded[product.productId] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={expanded[product.productId]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {product.items.map((item) => (
                  <ListItemButton
                    key={item.path}
                    sx={{
                      px: "26px",
                      borderRadius: 0,
                      height: "48px",
                      gap: "16px",
                      backgroundColor: isActive(item.path)
                        ? "rgba(40, 178, 172, 0.12)"
                        : "transparent",
                      "&.Mui-selected": {
                        backgroundColor: "rgba(40, 178, 172, 0.12)",
                        "&:hover": {
                          backgroundColor: "rgba(40, 178, 172, 0.18)",
                        },
                      },
                      "&:hover": {
                        backgroundColor: isActive(item.path)
                          ? "rgba(40, 178, 172, 0.18)"
                          : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                    selected={isActive(item.path)}
                    onClick={() => navigate(item.path)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "24px",
                        color: isActive(item.path)
                          ? "rgba(0, 0, 0, 0.87)"
                          : "rgba(0, 0, 0, 0.38)",
                      }}
                    >
                      <item.icon sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        sx: {
                          fontFamily: "Libre Franklin, sans-serif",
                          fontWeight: 500,
                          fontSize: "16px",
                          lineHeight: "19px",
                          letterSpacing: "0.5px",
                          color: "rgba(0, 0, 0, 0.87)",
                        },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
      {user && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.brand.primary,
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {user.first_name?.[0]}{user.last_name?.[0]}
            </Avatar>
            <Box sx={{ overflow: "hidden" }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#1E1E1E",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.first_name} {user.last_name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  lineHeight: "16px",
                  color: "#878787",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.email}
              </Typography>
            </Box>
          </Box>
        </>
      )}
      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          onClick={handleLogout}
          endIcon={<LogoutIcon sx={{ color: "rgba(0, 0, 0, 0.38)" }} />}
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "8px 16px",
            gap: "8px",
            height: "36px",
            background: "#FFFFFF",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
            fontFamily: "Inter, sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "20px",
            textAlign: "center",
            letterSpacing: "-0.150391px",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              background: "#f5f5f5",
              border: "1px solid rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          Log out
        </Button>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
